/**
 * ManualTreeNode Component
 *
 * Recursive tree node component for manual navigation.
 * Renders a single node with expand/collapse functionality and children.
 * Story 1-3: ManualTreeSidebar
 */

import { memo, useRef, useEffect } from 'react';
import type { TreeNode } from '../../lib/manualApi';

/** Indentation size per nesting level in pixels */
const TREE_INDENT_PX = 16;
/** Base left padding for tree nodes in pixels */
const TREE_BASE_PADDING_PX = 8;

/**
 * ChevronRight icon component (inline SVG)
 */
function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * ChevronDown icon component (inline SVG)
 */
function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

interface ManualTreeNodeProps {
  node: TreeNode;
  depth: number;
  activeSlug: string | undefined;
  /** Whether this specific node is expanded */
  isExpanded: boolean;
  /** Set of expanded node IDs for passing to children */
  expandedNodes: Set<number>;
  onToggle: (nodeId: number) => void;
  onNavigate: (slug: string) => void;
  /** Callback for focus management during keyboard navigation */
  onFocusSibling?: (direction: 'up' | 'down') => void;
}

/**
 * Get font weight based on section type
 */
function getSectionTypeStyle(sectionType: TreeNode['sectionType']): string {
  switch (sectionType) {
    case 'part':
      return 'font-bold text-gray-900';
    case 'chapter':
      return 'font-semibold text-gray-800';
    case 'section':
      return 'font-medium text-gray-700';
    case 'subsection':
      return 'text-gray-600';
    default:
      return 'text-gray-700';
  }
}

/**
 * ManualTreeNode - Recursive tree node with expand/collapse
 */
function ManualTreeNodeComponent({
  node,
  depth,
  activeSlug,
  isExpanded,
  expandedNodes,
  onToggle,
  onNavigate,
  onFocusSibling,
}: ManualTreeNodeProps) {
  const isActive = node.slug === activeSlug;
  const hasChildren = node.children.length > 0;
  const nodeRef = useRef<HTMLButtonElement>(null);

  // Focus management: scroll active node into view when it becomes active
  useEffect(() => {
    if (isActive && nodeRef.current && nodeRef.current.scrollIntoView) {
      nodeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isActive]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  const handleNavigate = () => {
    onNavigate(node.slug);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleNavigate();
        break;
      case 'ArrowRight':
        e.preventDefault();
        if (hasChildren && !isExpanded) {
          onToggle(node.id);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (hasChildren && isExpanded) {
          onToggle(node.id);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        onFocusSibling?.('up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        onFocusSibling?.('down');
        break;
    }
  };

  return (
    <li
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isActive}
      aria-level={depth + 1}
      data-testid="tree-node"
      data-active={isActive ? 'true' : undefined}
    >
      <button
        ref={nodeRef}
        type="button"
        className={`
          flex items-center w-full py-1.5 px-2 text-left rounded-md transition-colors
          ${isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'}
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
        `}
        style={{ paddingLeft: `${depth * TREE_INDENT_PX + TREE_BASE_PADDING_PX}px` }}
        onClick={handleNavigate}
        onKeyDown={handleKeyDown}
        data-testid={`tree-node-${node.slug}`}
        aria-current={isActive ? 'page' : undefined}
      >
        {hasChildren ? (
          <span
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onToggle(node.id);
              }
            }}
            role="button"
            tabIndex={-1}
            className="mr-1 p-0.5 rounded hover:bg-gray-200"
            aria-label={isExpanded ? 'Inklappen' : 'Uitklappen'}
            data-testid="tree-node-toggle"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </span>
        ) : (
          <span className="w-5 mr-1" aria-hidden="true" />
        )}
        <span
          id={`tree-node-label-${node.id}`}
          className={`text-sm truncate ${getSectionTypeStyle(node.sectionType)} ${
            isActive ? '!text-blue-700 !font-semibold' : ''
          }`}
          title={node.title}
          data-testid="tree-node-title"
          data-slug={node.slug}
        >
          {node.title}
        </span>
      </button>
      {isExpanded && hasChildren && (
        <ul role="group" aria-labelledby={`tree-node-label-${node.id}`} data-testid="tree-node-children">
          {node.children.map((child) => (
            <ManualTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              activeSlug={activeSlug}
              isExpanded={expandedNodes.has(child.id)}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// Memoize for performance with large trees
export const ManualTreeNode = memo(ManualTreeNodeComponent);
